import { EventEmitter, Injectable, Output } from "@angular/core";
import { HttpClient, HttpResponse } from "@angular/common/http";
import { catchError, firstValueFrom, Observable, of, Subject, tap } from "rxjs";
import { AuthenticationResult } from "../models/authentication-result.model";
import { MobileAuthenticateData, MobileAuthenticationStatus } from "../models/mobile-authenticate-data.model";
import { SmartIdAuthenticateData } from "../models/smart-id-authenticate-data.model";
import { SessionContext } from "../models/session-context.model";
import { environment } from "src/environments/environment";
import { FetchClassificatorsService } from "./fetch-classificators.service";
import { IdCardAuthenticateData } from "../models/id-card-authenticate-data.model";
import { UIModule } from "../models/ui-module.model";
import { AuthService } from "./auth.service";
import { UserInfoService } from "./user-info.service";
import { ModalService } from "./modal.service";
import { DeviceDetectService } from "./device-detect.service";
import { ThirdPartyLoginService } from "./third-party-login.service";
import { GoogleAuthenticateData } from "../models/google.authenticate-data.model";
import * as webeid from "@web-eid/web-eid-library/web-eid";
import { Application } from "../models/application.model";
import { NgxSpinnerService } from "ngx-spinner";
import { map } from "rxjs/operators";
import {
  BiometricsAuthenticateData,
  BiometricsAuthenticationMode,
  BiometricsAuthenticationStatus,
} from "../models/biometrics.authenticate-data.model";
import { ThirdPartyConnectionService } from "./third-party-connection-service";
import { Router } from "@angular/router";
import { WebAuthnService } from "./web-authn.service";
import { SessionStorageService } from "./session-storage.service";
import { ErrorCodes } from "../shared/constants/error-codes.enum";
import { GlobalErrorAndExceptionHandler } from "../shared/error-handler/global-error-and-exception-handler.service";
import { AuthType } from "../models/auth-type.model";
import { CookieManagerService } from "./cookie-manager.service";

@Injectable({
  providedIn: "root",
})
export class AuthenticateService {
  baseAndContextPath = environment.backend.baseUrl + environment.backend.contextPath;
  mockAuthUrl = this.baseAndContextPath + "/rest/mock-authentication/";
  mobIdUrl = this.baseAndContextPath + "/rest/authenticate/mob-id/";
  smartIdUrl = this.baseAndContextPath + "/rest/authenticate/smart-id/";
  googleUrl = this.baseAndContextPath + "/rest/authenticate/google/";
  idCardUrl = this.baseAndContextPath + "/rest/authenticate/id-card/";
  sessionUrl = this.baseAndContextPath + "/rest/get-session-context/";
  biometricsUrl = this.baseAndContextPath + "/rest/authenticate/biometrics/";
  sessionContext: SessionContext;
  authUserStatus = new Subject<number>();
  uiModule: UIModule;
  private tryOnce: boolean = true;
  private isBiometricsInitialized = false;

  @Output() googleAuthResponse: EventEmitter<any> = new EventEmitter();
  @Output() biometricsAuthResponse: EventEmitter<any> = new EventEmitter();

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private userInfoService: UserInfoService,
    private modalService: ModalService,
    private fetchClassificatorsService: FetchClassificatorsService,
    private thirdPartyLoginService: ThirdPartyLoginService,
    private spinner: NgxSpinnerService,
    deviceDetect: DeviceDetectService,
    private thirdPartyConnectionService: ThirdPartyConnectionService,
    private webAuthnService: WebAuthnService,
    private cookieManagerService: CookieManagerService,
    private router: Router,
    private sessionStorageService: SessionStorageService,
    private globalErrorHandler: GlobalErrorAndExceptionHandler,
  ) {
    this.uiModule = deviceDetect.isMobile ? UIModule.MOBILE : UIModule.DESKTOP;
  }

  /**
   * Initialize mobile authentication.
   * @param personalCode
   * @param phoneNumber
   */
  initMidAuthentication(personalCode: string, phoneNumber: string): Observable<MobileAuthenticateData | void> {
    const mobileAuthData: MobileAuthenticateData = {
      number: phoneNumber,
      personalCode: personalCode,
      application: Application.PATIENT,
      uiModule: this.uiModule,
      mode: "INIT",
    };
    return this.http
      .post<AuthenticationResult>(this.mobIdUrl, mobileAuthData, {
        withCredentials: true,
        observe: "response",
      })
      .pipe(
        map((response) => {
          if (response.status === 200 && response.body.acknowledgment.responseCode === "ACC") {
            return response.body.mobileAuthenticateData;
          } else {
            this.globalErrorHandler.handleBusinessResponse(response, ErrorCodes.ERR013, false, true);
            return null;
          }
        }),
        catchError((err) => {
          if (err.error.message) {
            this.modalService.showErrorModal(err.error.message, ErrorCodes.ERR014, true);
          } else {
            this.modalService.showErrorModal("ConnectionRefusedError", ErrorCodes.ERR015, true);
          }
          return of(null);
        })
      );
  }

  /**
   * Authenticate mobile ID and log in user.
   * @param mobAuthData
   */
  authenticateMidAndLogin(mobAuthData: MobileAuthenticateData): Observable<any> {
    mobAuthData.mode = "STATUS";
    return this.http
      .post<AuthenticationResult>(this.mobIdUrl, mobAuthData, {
        withCredentials: true,
        observe: "response",
      })
      .pipe(
        map((response) => {
          if (response.status === 200 && response.body.acknowledgment.responseCode === "ACC") {
            if (response.body.mobileAuthenticateData.status && response.body.mobileAuthenticateData.status === MobileAuthenticationStatus.USER_AUTHENTICATED) {
              // Call getSessionContext() but ignore its result. The catch block is added to handle and log
              // any potential errors from getSessionContext(), preventing unhandled promise rejection warnings.
              this.getSessionContext().catch(err => console.error(err));
            }
            return response.body.mobileAuthenticateData;
          } else {
            this.globalErrorHandler.handleBusinessResponse(response, ErrorCodes.ERR016, false, true )
            return null;
          }
        }),
        // TODO: once MOBILE-ID fix is implemented, set showErrorModal showErrorReport -> true
        catchError(() => {
          this.modalService.showErrorModal("MidAuthenticationFailed", ErrorCodes.ERR017, false);
          return of(null);
        })
      );
  }

  /**
   * Initialize smart ID authentication.
   * @param personalCode
   */
  async initSmartIdAuthentication(personalCode: string): Promise<SmartIdAuthenticateData | null> {
    try {
      const smartIdAuthenticateData: SmartIdAuthenticateData = {
        personalCode: personalCode,
        application: Application.PATIENT,
        uiModule: this.uiModule,
        mode: "INIT",
      };

      const response = await firstValueFrom(
        this.http.post<AuthenticationResult>(this.smartIdUrl, smartIdAuthenticateData, {
          withCredentials: true,
          observe: "response",
        }),
      );

      if (response.body.acknowledgment.responseCode === "ACC" && response.status === 200) {
        return response.body.smartIdAuthenticateData;
      } else {
        this.globalErrorHandler.handleBusinessResponse(response, ErrorCodes.ERR018, false, true);
        return null;
      }
    } catch (error) {
      console.error("Error in startSmartIdAuthentication:", error);
      this.modalService.showErrorModal("ConnectionRefusedError", ErrorCodes.ERR019, true);
      return null;
    }
  }

  /**
   * Authenticate smart ID and log in user.
   * @param smartIdAuthData
   */
  async authenticateSmartIdAndLogin(smartIdAuthData: SmartIdAuthenticateData): Promise<SmartIdAuthenticateData | void> {
    smartIdAuthData.mode = "STATUS";
    try {
      const response = await firstValueFrom(this.http.post<AuthenticationResult>(this.smartIdUrl, smartIdAuthData, {
        withCredentials: true,
        observe: "response",
      }));

      if (response.body.acknowledgment.responseCode === "ACC" && response.status === 200) {
        const smartIdAuthenticateData = response.body.smartIdAuthenticateData;
        if (smartIdAuthenticateData.status === MobileAuthenticationStatus.USER_AUTHENTICATED) {
          // Call getSessionContext() but ignore its result. The catch block is added to handle and log
          // any potential errors from getSessionContext(), preventing unhandled promise rejection warnings.
          try {
            await this.getSessionContext();
          } catch (err) {
            console.error(err);
          }
        }
        return smartIdAuthenticateData;
      } else {
        this.globalErrorHandler.handleBusinessResponse(response, ErrorCodes.ERR020, true, true);
        return null;
      }
    } catch (err) {
      // The emphasis is here. The reason for this "if case" is that when the user opens
      // the smart-id app and ePak goes to the background, the ePak frontend code is put on PAUSE.
      // This is common with mobile phones and also browsers on pc. When the user returns to ePak the
      // code flow resumes, but while away the ePak backend sends the response,
      // which ePak frontend is unable to receive and thus giving us the error, when we return to the ePak app.
      // So in this case we will not show the error just yet, but will try to authenticate once more.
      if (this.tryOnce) {
        this.tryOnce = false;
        await this.authenticateSmartIdAndLogin(smartIdAuthData);
      } else {
        this.globalErrorHandler.handleAndDisplayError(err, ErrorCodes.ERR021);
      }
      return null;
    }
  }

  /**
   * Initiates the registration or authentication process for biometrics depending on the current user's state.
   * If `biometricsLoginUUID` exists, it proceeds with authentication; otherwise, it starts the registration process.
   *
   * @return {Promise<HttpResponse<AuthenticationResult>>} A promise resolving to the HTTP response containing the authentication result.
   * If biometrics authentication status requires a hard authentication (`HARD_AUTH_NEEDED`), it deletes the biometrics login UUID and returns null.
   */
  async initBiometricsRegistrationOrAuthentication(): Promise<HttpResponse<AuthenticationResult>> {
    const biometricsLoginUUID = this.thirdPartyLoginService.biometricsLoginUUID;
    const biometricsAuthenticateData: BiometricsAuthenticateData = {
      application: Application.PATIENT,
      uiModule: this.uiModule,
      biometricsLoginUUID: biometricsLoginUUID,
      mode: this.thirdPartyLoginService.biometricsLoginUUID
        ? BiometricsAuthenticationMode.START_AUTH
        : BiometricsAuthenticationMode.START_REG,
    };
    const response = await this.makeHttpRequest <AuthenticationResult>(
      this.biometricsUrl,
      biometricsAuthenticateData,
      "Error in initiating biometrics login"
    );
    if (response.status === 200 && response.body) {
      const biometricData = response.body?.biometricsAuthenticateData;
      if (biometricData.status === BiometricsAuthenticationStatus.HARD_AUTH_NEEDED) {
        this.cookieManagerService.deleteLoginUUIDForAuthType(AuthType.BIOMETRICS);
        return null;
      }
    }
    return response;
  }

  /**
   * Registers or authenticates using biometrics and logs the user in.
   * This method handles biometric registration or authentication based on the current state
   * and returns the response status or executes respective operations based on the biometric result.
   * @param {any} credential - The biometric credential data used for registration or authentication.
   * @return {Promise<void>} A promise that resolves when the biometric process and any subsequent user session handling is completed.
   */
  async registerOrAuthenticateBiometricsAndLogin(credential: any): Promise<void> {
    const biometricsLoginUUID = this.thirdPartyLoginService.biometricsLoginUUID;
    const serializedCredential = this.webAuthnService.serializeCredential(credential);

    const biometricsAuthenticateData: BiometricsAuthenticateData = {
      application: Application.PATIENT,
      uiModule: this.uiModule,
      biometricsLoginUUID: biometricsLoginUUID,
      mode: this.thirdPartyLoginService.biometricsLoginUUID
        ? BiometricsAuthenticationMode.FINALIZE_AUTH
        : BiometricsAuthenticationMode.FINALIZE_REG,
      credential: JSON.stringify(serializedCredential),
      customDeviceName: this.thirdPartyConnectionService.customBrowserName,
    };
    const response = await this.makeHttpRequest<AuthenticationResult>(
      this.biometricsUrl,
      biometricsAuthenticateData,
      "HTTP error during biometrics authentication"
    );

    if (response.status === 200 && response.body) {
      const biometricData = response.body?.biometricsAuthenticateData;
      if (biometricData?.status === BiometricsAuthenticationStatus.REGISTRATION_COMPLETE) {
        await this.handleRegistrationComplete(response);
      } else if (biometricData?.status === BiometricsAuthenticationStatus.USER_AUTHENTICATED) {
        await this.getSessionContext();
      } else {
        this.globalErrorHandler.handleBusinessResponse(response, ErrorCodes.ERR022, false, true);
      }
    }
  }

  /**
   * Handles the completion of a registration process by processing the response, saving necessary data,
   * and updating the session context. Also setting the value of BiometricsRegDone for tracking in userProfile component.
   *
   * @param {HttpResponse<AuthenticationResult>} response - The HTTP response containing authentication result data,
   * including biometric login UUID.
   * @return {Promise<void>} A promise that resolves when the session context is updated.
   */
  private async handleRegistrationComplete(response: HttpResponse<AuthenticationResult>) {
    const uuid = response.body.biometricsAuthenticateData.biometricsLoginUUID;
    this.cookieManagerService.saveLoginUUIDForAuthType(uuid, AuthType.BIOMETRICS);
    await this.getSessionContext(false);
    if (this.router.url.endsWith('user-profile') || this.router.url.endsWith("contact-info")) {
      this.thirdPartyLoginService.setBiometricsRegDone(true);
    }
  }

  /**
   * Sends an HTTP POST request to the specified URL with the provided data.
   * Handles the response and potential errors.
   *
   * @param {string} url - The endpoint URL to send the POST request to.
   * @param {any} data - The payload to include in the POST request body.
   * @param {string} errorMessage - The error message to log in case the request fails.
   * @return {Promise<HttpResponse>} - A promise that resolves to the HTTP response containing the data of type T.
   */
  private async makeHttpRequest<T>(url: string, data: any, errorMessage: string):Promise<HttpResponse<T>> {
    try {
      return await firstValueFrom(
        this.http.post<T>(url, data, {
            withCredentials: true,
            responseType: "json",
            observe: "response",
      })
      );
    } catch (err) {
      console.error(errorMessage, err);
      this.modalService.showErrorModal("BiometricsAuthenticationFailed", ErrorCodes.ERR023, false);
    }
  }

  /**
   * Authenticate mock user and log in user.
   * @param personalCode
   */
  authenticateMockAndLogin(personalCode): Observable<any> {
    if (environment.profiles.includes("mockLogin")) {
      const mobileAuthData: MobileAuthenticateData = {
        number: "37200000766",
        personalCode: personalCode,
        application: Application.PATIENT,
        uiModule: this.uiModule,
        mode: "INIT",
      };
      return this.http.post<AuthenticationResult>(this.mockAuthUrl, mobileAuthData, {
        withCredentials: true,
        observe: "response",
      }).pipe(
        tap((response) => {
          if (response.body.acknowledgment.responseCode === "ACC") {
            // Call getSessionContext() but ignore its result. The catch block is added to handle and log
            // any potential errors from getSessionContext(), preventing unhandled promise rejection warnings.
            this.getSessionContext().catch(err => console.error(err));
          } else {
            this.authUserStatus.next(0);
            this.globalErrorHandler.handleBusinessResponse(response, ErrorCodes.ERR024, true, true);
          }
        })
      )
    }
  }

  /**
   * Authenticate ID card and log in user.
   */
  async authenticateIdCardAndLogin() {
    try {
      const challengeResponse = await firstValueFrom(
        this.http.get<any>(this.idCardUrl + "challenge", { withCredentials: true, observe: "response" }),
      );
      if (challengeResponse) {
        let challengeNonce = challengeResponse.body.nonce;
        let authToken = JSON.stringify(await webeid.authenticate(challengeNonce));
        let idCardAuthenticateData: IdCardAuthenticateData = {
          application: Application.PATIENT,
          uiModule: this.uiModule,
          authToken: authToken,
        };
        await this.spinner.show();
        const response = await firstValueFrom(
          this.http.post<AuthenticationResult>(this.idCardUrl + "auth", idCardAuthenticateData, {
            withCredentials: true,
            responseType: "json",
            observe: "response",
          }),
        );
        if (response.status === 200 && response.body.acknowledgment.responseCode === "ACC") {
          this.authService.setIdCardAuthenticate(true);
          await this.getSessionContext();
          await this.spinner.hide();
        } else {
          await this.spinner.hide();
          this.globalErrorHandler.handleBusinessResponse(response, ErrorCodes.ERR025, false, true);
        }
      }
    } catch (error) {
      console.error("Error during ID card authentication:", error);
    }
  }

  /**
   * Authenticate Google and log in user.
   * @param credentials
   * @param googleLoginUUID
   */
  async authenticateGoogleAndLogin(credentials: string, googleLoginUUID: string) {
    try {
      const googleAuthenticateData: GoogleAuthenticateData = {
        application: Application.PATIENT,
        uiModule: this.uiModule,
        credentials: credentials,
        googleLoginUUID: googleLoginUUID,
      };
      let response = await firstValueFrom(
        this.http.post<AuthenticationResult>(this.googleUrl, googleAuthenticateData, {
          withCredentials: true,
          responseType: "json",
          observe: "response",
        }),
      );

      if (response.status === 200) {
        const code = response.body.acknowledgment.responseCode;
        if (code === "ACC") {
          await this.getSessionContext();
          this.googleAuthResponse.emit(response);
        } else if (code === "HARD_AUTH_NEEDED") {
          this.googleAuthResponse.emit(response);
        } else {
          this.globalErrorHandler.handleBusinessResponse(response, ErrorCodes.ERR003, false, true,);
        }
      }
    } catch (error) {
      console.error("Error in authenticating google login: ", error);
      this.modalService.showErrorModal("GoogleAuthenticationFailed", ErrorCodes.ERR026, false);
      return null;
    }
  }

  /**
   * Get session ID and save it to sessionStorage.
   */
  async getSessionId() {
    try {
      const response: HttpResponse<SessionContext> = await firstValueFrom(
        this.http.get<SessionContext>(this.sessionUrl, {
          withCredentials: true,
          observe: "response",
        }),
      );

      // if patient session is already logged in, then redirect to the main page.
      await this.checkPreviousPatientSession(response);

      // save sessionId to sessionStorage
      sessionStorage.setItem("sessionId", response.body.sessionId);
      // save application to sessionStorage
      sessionStorage.setItem("application", response.body.application);
    } catch (error) {
      // Handle any errors that might occur during the request
      console.error("Error:", error);
    }
  }

  /**
   * Check if patient session is already logged in.
   * @param response
   */
  private async checkPreviousPatientSession(response: HttpResponse<SessionContext>) {
    if (response.status === 200 && response.body.personalCode !== null &&
      response.body.application === Application.PATIENT) {
      this.sessionContext = response.body;
      this.userInfoService.setSessionContext(this.sessionContext);
      await this.fetchClassificatorsService.fetchAllTypeIds();
      if (this.sessionContext.termsOutput !== null && this.sessionContext.termsOutput.termsAccepted &&
        this.sessionContext.termsOutput.acceptedTermsValid) {
        // existing user
        this.authService.login();
      }
    }
  }

  /**
   * Get session context and redirect to start page or restore session context.
   * @param redirectToStartPage
   */
  async getSessionContext(redirectToStartPage = true): Promise<void> {
    // Fetch classification type IDs before proceeding
    if (!(await this.fetchClassificatorsService.fetchAllTypeIds())) {
      return this.handleError("errorBackup");
    }

    try {
      // Fetch session context from the server
      const response = await this.fetchSessionContext();

      // Validate response status and user data
      if (response.status !== 200 || !response.body?.personalCode) {
        return this.handleError("cannotFindUser");
      }

      // Store session data and update services
      this.handleSessionStorage(response.body);
      this.sessionContext = response.body;
      this.authUserStatus.next(response.status);
      this.userInfoService.sessionContext.next(this.sessionContext);

      // Redirect user to start page or restore session context
      if (redirectToStartPage) {
        await this.handleRedirectToStartPage();
      } else {
        this.restoreSessionContext();
      }
    } catch {
      return this.handleError("errorBackup");
    }
  }

  /**
   * Initialize external login connections, such as Google authentication.
   * @return {Promise<void>} A promise that resolves when the external login connection is initialized.
   */
  async initExternalLoginConnection() {
    if (this.sessionContext.googleAuthenticateData !== null) {
      await this.thirdPartyLoginService.saveGoogleLoginConsent(
        true,
        this.sessionContext.googleAuthenticateData);
    }
    if (!this.isBiometricsInitialized && this.thirdPartyConnectionService.connectUserWithBiometrics) {
      this.isBiometricsInitialized = true;
      await this.thirdPartyLoginService.handleBiometricsLoginResponse(this.initBiometricsRegistrationOrAuthentication());
    }
  }


  /**
   * Fetches the session context from the backend API.
   * @return {Promise<HttpResponse<SessionContext>>} A promise that resolves to the HTTP response containing the session context.
   * @private
   */
  private async fetchSessionContext(): Promise<HttpResponse<SessionContext>> {
    return firstValueFrom(
      this.http.get<SessionContext>(this.sessionUrl, {
        withCredentials: true,
        observe: "response",
      }),
    );
  }

  /**
   * Handles the storage of session context in session storage.
   * @param sessionContext
   * @private
   */
  private handleSessionStorage(sessionContext: SessionContext): void {
    sessionStorage.setItem("sessionId", sessionContext.sessionId);
    sessionStorage.setItem("sessionContext", JSON.stringify(sessionContext));
  }

  /**
   * Restores the session context from session storage.
   * @return {void}
   * @private
   */
  private restoreSessionContext(): void {
    const storedContext = sessionStorage.getItem("sessionContext");
    if (storedContext) {
      this.sessionContext = JSON.parse(storedContext);
      this.userInfoService.sessionContext.next(this.sessionContext);
    }
  }

  /**
   *  Redirects the user to the appropriate start page based on the session context.
   * @return {Promise<void>} A promise that resolves when the redirection is complete.
   * @private
   */
  private async handleRedirectToStartPage(): Promise<void> {
    if (!this.sessionContext) {
      return this.handleError("errorBackup");
    }

    const { termsOutput } = this.sessionContext;

    // Save external login connection details if applicable
    await this.initExternalLoginConnection();

    // Handle login based on terms acceptance
    if (termsOutput?.termsAccepted && termsOutput.acceptedTermsValid) {
      this.authService.login(); // Existing user login
    } else {
      this.authService.newUserLogin(); // New user login
    }
    this.sessionStorageService.clearBiometricCancellationFlag();
  }

  /**
   * Handles errors by showing an error modal and clearing the session.
   * @return {Promise<void>} A promise that resolves when the error handling is complete.
   * @param modalKey
   * @private
   */
  private async handleError(modalKey: string): Promise<void> {
    this.modalService.showErrorModal(modalKey, ErrorCodes.ERR027, true);
    await this.authService.clearSession();
  }
}
